import { useState, Suspense, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { MobileNav } from '@/components/MobileNav';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useSession } from '@/hooks/use-session';
import { useSubscription } from '@/hooks/use-subscription';
import { Session } from '@supabase/supabase-js';

// Types for the Market Screener API
interface MarketOpportunity {
  name?: string;
  symbol?: string;
  price_usd?: number;
  price_change_24h?: number;
  market_cap?: number;
  volume_24h?: number;
  image?: string;
  strykr_analysis?: string;
  recommended_action: "LONG" | "SHORT";
  signal_rating?: number;
  risk_level?: "LOW" | "MEDIUM" | "HIGH" | "EXTREME";
  entry_price_target?: number;
  stop_loss_target?: number;
  take_profit_targets?: number[];
  trade_timing?: string;
  heatmap_color?: string;
  heatmap_intensity?: number;
  trend_arrow?: string;
  momentum_score?: number;
  volatility_score?: number;
  confidence_level?: "LOW" | "MEDIUM" | "HIGH";
  network?: string;
  performance_category?: string;
}

interface MarketScreenerResponse {
  opportunities: MarketOpportunity[];
  timestamp: string;
  status: string;
  error?: string;
}

// Fetch market screener data from Supabase Edge Function
const fetchMarketScreenerData = async (session?: Session | null): Promise<MarketScreenerResponse> => {
  try {
    console.log('🔍 Calling Supabase edge function: screener');
    console.log('⏰ Starting at:', new Date().toISOString());
    console.log('👤 Session available:', !!session?.user?.id);
    
    // Check if user is authenticated
    if (!session?.user?.id) {
      console.error('🚨 No authenticated user - cannot fetch market data');
      return {
        opportunities: [],
        timestamp: new Date().toISOString(),
        status: 'error',
        error: 'Please sign in to view market screening data.'
      };
    }
    
    const { data, error } = await supabase.functions.invoke('screener');
    
    console.log('📊 Edge function response received');
    console.log('⏰ Completed at:', new Date().toISOString());
    console.log('✅ Data:', data);
    console.log('❌ Error:', error);
    
    if (error) {
      console.error('🚨 Edge function error:', error);
      
      // Check if this is a FunctionsHttpError (edge function returned non-2xx status)
      if (error.message?.includes('Edge Function returned a non-2xx status code') || 
          error.name === 'FunctionsHttpError') {
        console.error('🚨 Edge function returned error status code - providing fallback data');
        
        // Return fallback data structure to prevent page crash
        return {
          opportunities: [],
          timestamp: new Date().toISOString(),
          status: 'error',
          error: 'Market data temporarily unavailable. Our servers are experiencing high demand. Please try again in a few minutes.'
        };
      }
      
      throw new Error(`Edge function error: ${error.message}`);
    }
    
    if (!data) {
      console.error('🚨 No data returned from edge function');
      
      // Return fallback data instead of throwing
      return {
        opportunities: [],
        timestamp: new Date().toISOString(),
        status: 'empty',
        error: 'No market opportunities available at this time. Please check back later.'
      };
    }
    
    // Handle both array and object responses
    let formattedResponse: MarketScreenerResponse;
    
    if (Array.isArray(data)) {
      // If the response is an array, wrap it in the expected format
      console.log('📋 Response is an array, wrapping it');
      formattedResponse = {
        opportunities: data,
        timestamp: new Date().toISOString(),
        status: 'success'
      };
    } else {
      // If it's already an object, use it as is
      console.log('📋 Response is an object');
      formattedResponse = data;
    }
    
    // Log the structure of the formatted data
    console.log('📋 Formatted data structure:');
    console.log('- opportunities:', formattedResponse.opportunities?.length || 'undefined');
    console.log('- timestamp:', formattedResponse.timestamp || 'undefined');
    console.log('- status:', formattedResponse.status || 'undefined');
    
    // Sanitize market opportunity data by replacing null numeric and array values with undefined
    if (formattedResponse.opportunities && Array.isArray(formattedResponse.opportunities)) {
      console.log('🧹 Sanitizing opportunity data to prevent toFixed() errors');
      formattedResponse.opportunities = formattedResponse.opportunities.map(op => {
        const sanitizedOp = { ...op };
        
        // Replace null numeric values with undefined
        if (sanitizedOp.price_usd === null) sanitizedOp.price_usd = undefined;
        if (sanitizedOp.price_change_24h === null) sanitizedOp.price_change_24h = undefined;
        if (sanitizedOp.market_cap === null) sanitizedOp.market_cap = undefined;
        if (sanitizedOp.volume_24h === null) sanitizedOp.volume_24h = undefined;
        if (sanitizedOp.signal_rating === null) sanitizedOp.signal_rating = undefined;
        if (sanitizedOp.entry_price_target === null) sanitizedOp.entry_price_target = undefined;
        if (sanitizedOp.stop_loss_target === null) sanitizedOp.stop_loss_target = undefined;
        if (sanitizedOp.heatmap_intensity === null) sanitizedOp.heatmap_intensity = undefined;
        if (sanitizedOp.momentum_score === null) sanitizedOp.momentum_score = undefined;
        if (sanitizedOp.volatility_score === null) sanitizedOp.volatility_score = undefined;
        
        // Handle take_profit_targets array
        if (sanitizedOp.take_profit_targets === null) {
          sanitizedOp.take_profit_targets = undefined;
        } else if (Array.isArray(sanitizedOp.take_profit_targets)) {
          sanitizedOp.take_profit_targets = sanitizedOp.take_profit_targets.map(target =>
            target === null ? undefined : target
          );
        }
        
        return sanitizedOp;
      });
      console.log('✅ Data sanitization complete');
    }
    
    return formattedResponse;
    
  } catch (error) {
    console.error('💥 Error in fetchMarketScreenerData:', error);
    
    // Check if this is a network/connection error
    if (error instanceof TypeError && error.message?.includes('fetch')) {
      console.error('🌐 Network error detected - returning fallback data');
      return {
        opportunities: [],
        timestamp: new Date().toISOString(),
        status: 'network_error',
        error: 'Unable to connect to market data service. Please check your internet connection and try again.'
      };
    }
    
    // For any other error, return fallback data instead of crashing
    return {
      opportunities: [],
      timestamp: new Date().toISOString(),
      status: 'error',
      error: 'An unexpected error occurred while loading market data. Please try refreshing the page.'
    };
  }
};

interface OpportunityCardProps {
  opportunity: MarketOpportunity;
  onClick: () => void;
  isSelected?: boolean;
}

const OpportunityCard = ({ opportunity, onClick, isSelected }: OpportunityCardProps) => {
  const isLong = opportunity.recommended_action === "LONG";
  const priceUsd = opportunity.price_usd ?? 0;
  const priceFormatted = priceUsd > 1 
    ? `$${priceUsd.toLocaleString()}` 
    : `$${priceUsd.toFixed(6)}`;
  
  return (
    <Card 
      className={cn(
        "relative group cursor-pointer transition-all duration-300 ease-out",
        "bg-gradient-to-br from-gray-900/90 to-gray-950/95 border border-gray-700/50",
        "hover:shadow-2xl hover:scale-[1.02] hover:border-blue-500/50",
        "min-h-[280px] p-4 overflow-hidden",
        isSelected && "ring-2 ring-blue-500 scale-[1.02]"
      )}
      style={{
        boxShadow: `0 0 ${20 * (opportunity.heatmap_intensity ?? 0)}px ${opportunity.heatmap_color ?? '#ffffff'}40`,
      }}
      onClick={onClick}
    >
      {/* Animated background glow */}
      <div 
        className="absolute inset-0 opacity-5 animate-pulse"
        style={{
          background: `radial-gradient(circle at center, ${opportunity.heatmap_color ?? '#ffffff'} 0%, transparent 70%)`,
        }}
      />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {opportunity.image ? (
            <img src={opportunity.image} alt={opportunity.symbol} className="w-8 h-8 rounded-full" />
          ) : (
            <span className="text-2xl font-mono">{opportunity.symbol?.slice(0, 2) ?? 'N/A'}</span>
          )}
          <div>
            <span className="font-bold text-white text-sm">{opportunity.symbol ?? 'N/A'}</span>
            <div className="text-xs text-gray-400">{opportunity.name ?? 'N/A'}</div>
          </div>
        </div>
        <Badge 
          className={cn(
            "text-xs font-bold",
            isLong ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"
          )}
        >
          {opportunity.recommended_action}
        </Badge>
      </div>

      {/* Price and Change */}
      <div className="mb-3">
        <div className="text-xl font-mono font-bold text-white">
          {priceFormatted}
        </div>
        <div className={cn(
          "text-sm font-mono flex items-center gap-1",
          (opportunity.price_change_24h ?? 0) >= 0 ? "text-green-400" : "text-red-400"
        )}>
          {(opportunity.price_change_24h ?? 0) >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {(opportunity.price_change_24h ?? 0) >= 0 ? "+" : ""}{(opportunity.price_change_24h ?? 0).toFixed(2)}%
        </div>
      </div>

      {/* Trading Setup Grid */}
      <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
        <div className="bg-gray-800/50 rounded p-2">
          <div className="text-gray-400">Entry</div>
          <div className="text-blue-300 font-mono">
            ${opportunity.entry_price_target 
              ? opportunity.entry_price_target.toFixed(opportunity.entry_price_target < 1 ? 6 : 2)
              : 'N/A'
            }
          </div>
        </div>
        <div className="bg-gray-800/50 rounded p-2">
          <div className="text-gray-400">Stop Loss</div>
          <div className="text-red-300 font-mono">
            ${opportunity.stop_loss_target 
              ? opportunity.stop_loss_target.toFixed(opportunity.stop_loss_target < 1 ? 6 : 2)
              : 'N/A'
            }
          </div>
        </div>
      </div>

      {/* Take Profit Targets */}
      <div className="mb-3">
        <div className="text-xs text-gray-400 mb-1">Take Profit</div>
        <div className="flex gap-1 flex-wrap">
          {(opportunity.take_profit_targets ?? []).slice(0, 3).map((target, i) => (
            <Badge key={i} variant="outline" className="text-xs text-green-300 border-green-500/40 px-1 py-0">
              ${target ? target.toFixed(target < 1 ? 4 : 2) : 'N/A'}
            </Badge>
          ))}
        </div>
      </div>

      {/* Metrics Bar */}
      <div className="space-y-2 mb-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400">Signal</span>
          <div className="flex items-center gap-2">
            <div className="w-20 bg-gray-700 rounded-full h-1.5">
              <div 
                className={cn(
                  "h-1.5 rounded-full transition-all duration-1000",
                  isLong ? "bg-green-500" : "bg-red-500"
                )}
                style={{ width: `${(Math.abs(opportunity.signal_rating ?? 0) / 1000) * 100}%` }}
              />
            </div>
            <span className="text-white font-mono">{Math.abs(opportunity.signal_rating ?? 0)}</span>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400">Momentum</span>
          <div className="flex items-center gap-2">
            <div className="w-20 bg-gray-700 rounded-full h-1.5">
              <div 
                className="h-1.5 rounded-full bg-blue-500 transition-all duration-1000"
                style={{ width: `${opportunity.momentum_score ?? 0}%` }}
              />
            </div>
            <span className="text-white font-mono">{opportunity.momentum_score ?? 0}</span>
          </div>
        </div>
      </div>

      {/* Bottom Info */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <Badge 
            variant="outline" 
            className={cn(
              "text-xs px-1 py-0",
              opportunity.risk_level === "LOW" ? "border-green-500/40 text-green-300" :
              opportunity.risk_level === "MEDIUM" ? "border-yellow-500/40 text-yellow-300" :
              opportunity.risk_level === "HIGH" ? "border-orange-500/40 text-orange-300" :
              "border-red-500/40 text-red-300"
            )}
          >
            {opportunity.risk_level ?? 'UNKNOWN'} RISK
          </Badge>
          <Badge 
            variant="outline" 
            className="text-xs border-blue-500/40 text-blue-300 px-1 py-0"
          >
            {opportunity.confidence_level ?? 'UNKNOWN'}
          </Badge>
        </div>
        <Clock className="w-3 h-3 text-gray-400" />
      </div>

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </Card>
  );
};

interface DetailedPanelProps {
  opportunity: MarketOpportunity;
  onClose: () => void;
}

const DetailedPanel = ({ opportunity, onClose }: DetailedPanelProps) => {
  const isLong = opportunity.recommended_action === "LONG";
  const priceUsd = opportunity.price_usd ?? 0;
  const priceFormatted = priceUsd > 1 
    ? `$${priceUsd.toLocaleString()}` 
    : `$${priceUsd.toFixed(6)}`;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {opportunity.image ? (
                <img src={opportunity.image} alt={opportunity.symbol} className="w-12 h-12 rounded-full" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center text-xl font-bold">
                  {opportunity.symbol?.slice(0, 2) ?? 'N/A'}
                </div>
              )}
              <div>
                <h2 className="text-xl font-bold text-white">{opportunity.name ?? 'N/A'}</h2>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-gray-400">{opportunity.symbol ?? 'N/A'}</span>
                  <Badge 
                    className={cn(
                      "text-xs",
                      isLong ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"
                    )}
                  >
                    {opportunity.recommended_action}
                  </Badge>
                </div>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-400 hover:text-white">
              ✕
            </Button>
          </div>

          {/* AI Analysis */}
          <div className="mb-6 p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
            <h3 className="flex items-center gap-2 text-sm font-bold text-white mb-3">
              <span className="text-lg">🤖</span>
              STRYKR AI ANALYSIS
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed">{opportunity.strykr_analysis ?? 'Analysis not available'}</p>
          </div>

          {/* Trading Details Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Market Stats */}
            <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
              <h4 className="text-xs font-bold text-gray-400 mb-3">MARKET STATS</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Market Cap</span>
                  <span className="text-white font-mono">
                    ${opportunity.market_cap ? (opportunity.market_cap / 1e9).toFixed(2) + 'B' : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">24h Volume</span>
                  <span className="text-white font-mono">
                    ${opportunity.volume_24h ? (opportunity.volume_24h / 1e6).toFixed(2) + 'M' : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Network</span>
                  <span className="text-white capitalize">{opportunity.network || 'Multiple'}</span>
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
              <h4 className="text-xs font-bold text-gray-400 mb-3">PERFORMANCE</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Volatility</span>
                  <span className="text-white font-mono">{opportunity.volatility_score ?? 0}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Category</span>
                  <span className="text-white text-xs">{opportunity.performance_category?.replace(/_/g, ' ') ?? 'N/A'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Trend</span>
                  <span className="text-2xl" style={{ color: opportunity.heatmap_color ?? '#ffffff' }}>
                    {opportunity.trend_arrow ?? '→'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Timing Alert */}
          <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30 mb-6">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-blue-300 mb-1">OPTIMAL TIMING</h4>
                <p className="text-sm text-gray-300">{opportunity.trade_timing ?? 'Timing information not available'}</p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

const MarketScreenerContent = () => {
  const [positionType, setPositionType] = useState<'all' | 'long' | 'short'>('all');
  const [selectedOpportunity, setSelectedOpportunity] = useState<MarketOpportunity | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [opportunities, setOpportunities] = useState<MarketOpportunity[]>([]);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();
  
  // Add session and subscription handling like working pages
  const { session } = useSession();
  // Ensure subscription context hook is called to maintain React hooks order
  useSubscription();

  const fetchData = async () => {
    try {
      setIsRefreshing(true);
      setError(null);

      // Pass session to the API call for proper authentication
      const data = await fetchMarketScreenerData(session);
      
      // Check if the response contains an error
      if (data.status === 'error' || data.status === 'network_error' || data.status === 'empty') {
        setError(data.error || 'Unknown error occurred');
        setOpportunities([]); // Set empty opportunities for error states
      } else {
        setOpportunities(data.opportunities);
        setError(null); // Clear any previous errors
      }
      
      setLastUpdated(new Date());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch market data';
      setError(errorMessage);
      setOpportunities([]); // Ensure opportunities is empty on error
    } finally {
      setIsRefreshing(false);
    }
  };

  // Initial data fetch - only when user is authenticated
  useEffect(() => {
    if (session?.user?.id) {
      fetchData();
    }
  }, [session?.user?.id]);

  // Auto-refresh every 2 minutes - only for authenticated users
  useEffect(() => {
    if (session?.user?.id) {
      const interval = setInterval(fetchData, 120000);
      return () => clearInterval(interval);
    }
  }, [session?.user?.id]);

  const filteredOpportunities = opportunities.filter(op => {
    if (positionType === 'all') return true;
    return positionType === 'long' 
      ? op.recommended_action === 'LONG' 
      : op.recommended_action === 'SHORT';
  });

  // Show authentication message if not logged in
  if (!session?.user?.id) {
    return (
      <div className={cn(
        "min-h-screen bg-gradient-to-br from-slate-950 via-gray-950 to-black",
        "container mx-auto p-4 max-w-7xl flex items-center justify-center",
        isMobile && "pb-24"
      )}>
        <div className="text-center space-y-4">
          <AlertCircle className="h-16 w-16 text-blue-400 mx-auto" />
          <h2 className="text-2xl font-bold text-white">Authentication Required</h2>
          <p className="text-gray-400 max-w-md">
            Please sign in to access the Market Screener and view AI-powered trading signals.
          </p>
          <Button
            onClick={() => window.location.href = '/auth'}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Sign In
          </Button>
        </div>
        {isMobile && <MobileNav />}
      </div>
    );
  }

  return (
    <div className={cn(
      "min-h-screen bg-gradient-to-br from-slate-950 via-gray-950 to-black",
      "container mx-auto p-4 max-w-7xl",
      isMobile && "pb-24"
    )}>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <img 
            src="/strykr-logo.jpeg" 
            alt="Strykr Logo" 
            className="h-10 w-10 rounded-lg object-cover"
          />
          <div>
            <h1 className="text-3xl font-bold text-white">Strykr AI</h1>
            <p className="text-gray-400 text-sm">
              Market Intelligence • AI-Powered Trading Signals
            </p>
          </div>
        </div>

        {/* Last Updated */}
        {lastUpdated && (
          <div className="text-xs text-gray-500 font-mono">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-3 mb-6 p-3 bg-gray-900/30 rounded-lg border border-gray-800/50">
        {/* Position Type Filter */}
        <Tabs value={positionType} onValueChange={(value) => setPositionType(value as 'all' | 'long' | 'short')}>
          <TabsList className="bg-gray-800/50 border border-gray-700 h-8">
            <TabsTrigger value="all" className="text-xs px-3 h-7">All</TabsTrigger>
            <TabsTrigger value="long" className="text-xs px-3 h-7 data-[state=active]:text-green-400">LONG</TabsTrigger>
            <TabsTrigger value="short" className="text-xs px-3 h-7 data-[state=active]:text-red-400">SHORT</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Stats */}
        <div className="flex items-center gap-3 ml-auto text-xs font-mono">
          <span className="text-green-400">
            LONG: {filteredOpportunities.filter(op => op.recommended_action === 'LONG').length}
          </span>
          <span className="text-gray-500">|</span>
          <span className="text-red-400">
            SHORT: {filteredOpportunities.filter(op => op.recommended_action === 'SHORT').length}
          </span>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-8 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <div className="flex items-center gap-3 text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span className="font-mono flex-1">{error}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={isRefreshing}
              className="border-red-500/50 hover:bg-red-500/20 text-red-400 hover:text-red-300"
            >
              {isRefreshing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Retrying...
                </>
              ) : (
                'Retry'
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isRefreshing && !opportunities.length && (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-400 mx-auto mb-4" />
            <div className="text-xl font-mono text-blue-300 mb-2">
              INITIALIZING MARKET DATA...
            </div>
            <div className="text-sm font-mono text-gray-400">
              AI analysis in progress • This may take up to 2 minutes
            </div>
          </div>
        </div>
      )}

      {/* Heatmap Grid or Empty State */}
      {!isRefreshing && !error && opportunities.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-xl font-mono text-gray-400">
            No trading opportunities found
          </div>
        </div>
      ) : opportunities.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
          {filteredOpportunities.map((opportunity, index) => (
            <OpportunityCard
              key={`${opportunity.symbol}-${index}`}
              opportunity={opportunity}
              onClick={() => setSelectedOpportunity(opportunity)}
              isSelected={selectedOpportunity?.symbol === opportunity.symbol}
            />
          ))}
        </div>
      ) : null}

      {/* Detailed Panel Modal */}
      {selectedOpportunity && (
        <DetailedPanel 
          opportunity={selectedOpportunity} 
          onClose={() => setSelectedOpportunity(null)} 
        />
      )}

      {/* Mobile Navigation */}
      {isMobile && <MobileNav />}
    </div>
  );
};

export default function MarketScreenerPage() {
  return (
    <ErrorBoundary>
      <Suspense 
        fallback={
          <div className="flex h-[80vh] items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Loading market screener...</p>
            </div>
          </div>
        }
      >
        <MarketScreenerContent />
      </Suspense>
    </ErrorBoundary>
  );
}